import {
  createSearchFlowTemplate,
  getSearchFlow,
  listPuckPages,
  upsertSearchFlow,
  type SearchFlowTarget,
} from "@/api/cms-puck/api";
import { useGeneralSettingsFormData } from "@/api/cms";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { CmsAdminShell } from "@/pages/cms/advanced/AdminShell";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ExternalLink, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCmsNew } from "@/lib/cms/cms-new-i18n";
import { cmsPhrase, puckPageStatusLabel } from "@/lib/cms/cms-new-phrases";

const FLOW_KEY = ["cms-puck", "search-flow"] as const;
const PAGES_KEY = ["cms-puck", "pages"] as const;
const NONE_VALUE = "__none__";

const TARGETS: { target: SearchFlowTarget }[] = [
  { target: "ARTWORKS" },
  { target: "MULTIPRINTS" },
];

function FlowCell({
  target,
  value,
  templatePages,
  onAssign,
  onCreateTemplate,
  saving,
  creating,
}: {
  target: SearchFlowTarget;
  value: string | null;
  templatePages: { id: string; name: string; status: string }[];
  onAssign: (pageId: string | null) => void;
  onCreateTemplate: () => void;
  saving: boolean;
  creating: boolean;
}) {
  const cmsNew = useCmsNew();
  const navigate = useNavigate();
  const assigned = templatePages.find((p) => p.id === value);
  const isDraft = assigned && assigned.status !== "PUBLISHED";
  const label =
    target === "ARTWORKS"
      ? cmsPhrase(cmsNew, "searchResultsArtworks")
      : cmsPhrase(cmsNew, "searchResultsMultiples");

  return (
    <div className="space-y-2 rounded-lg border border-slate-200 bg-white p-4">
      <Label className="text-sm font-medium text-slate-800">{label}</Label>
      <Select
        value={value ?? NONE_VALUE}
        onValueChange={(next) =>
          onAssign(next === NONE_VALUE ? null : next)
        }
        disabled={saving || creating}
      >
        <SelectTrigger>
          <SelectValue placeholder={cmsNew("cms.new.auto.placeholder.aucune_template_classique", "Aucune (template classique)")} />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE_VALUE}>{cmsNew("cms.new.auto.selectItem.aucune_template_classique", "Aucune (template classique)")}</SelectItem>
          {templatePages.map((page) => (
            <SelectItem key={page.id} value={page.id}>
              {page.name}
              {page.status !== "PUBLISHED" ? ` (${puckPageStatusLabel(cmsNew, page.status)})` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {isDraft ? (
        <p className="text-xs text-amber-700">
          {cmsPhrase(cmsNew, "draftPublishHint")}
        </p>
      ) : null}
      <div className="flex flex-wrap gap-2 pt-1">
        {value ? (
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-8 gap-1"
            onClick={() => navigate(`/cms/advanced/${value}`)}
          >
            <ExternalLink size={14} />
            {cmsPhrase(cmsNew, "editTemplate")}
          </Button>
        ) : (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-8 gap-1"
            disabled={creating}
            onClick={onCreateTemplate}
          >
            <Plus size={14} />
            {creating ? cmsPhrase(cmsNew, "creating") : cmsPhrase(cmsNew, "createTemplate")}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CMSAdvancedRecherche() {
  const cmsNew = useCmsNew();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: generalSettings } = useGeneralSettingsFormData();
  const isMixedCRMode = generalSettings?.isMixedCRMode ?? false;

  const flowQuery = useQuery({ queryKey: FLOW_KEY, queryFn: getSearchFlow });
  const pagesQuery = useQuery({ queryKey: PAGES_KEY, queryFn: () => listPuckPages() });

  const saveMutation = useMutation({
    mutationFn: upsertSearchFlow,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FLOW_KEY });
      void queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      toast({
        title: cmsNew("cms.new.auto.title.parcours_recherche_enregistre", "Parcours recherche enregistré"),
        variant: "success",
        duration: 1600,
      });
    },
    onError: () => {
      toast({
        title: cmsNew("cms.new.auto.title.impossible_denregistrer_la_liaison", "Impossible d'enregistrer la liaison"),
        variant: "error",
      });
    },
  });

  const createTemplateMutation = useMutation({
    mutationFn: createSearchFlowTemplate,
    onSuccess: ({ page }) => {
      void queryClient.invalidateQueries({ queryKey: FLOW_KEY });
      void queryClient.invalidateQueries({ queryKey: PAGES_KEY });
      toast({
        title: cmsNew("cms.new.auto.title.template_recherche_cree", "Template recherche créé"),
        variant: "success",
        duration: 1600,
      });
      navigate(`/cms/advanced/${page.id}`);
    },
    onError: (error: unknown) => {
      const message =
        error instanceof Error
          ? error.message
          : cmsPhrase(cmsNew, "createTemplateFailed");
      toast({ title: message, variant: "error" });
    },
  });

  const bindings = flowQuery.data ?? [];
  const pages = pagesQuery.data ?? [];
  const templatePages = pages.filter((p) => (p.searchFlowRoles?.length ?? 0) > 0);

  const pageIdFor = (target: SearchFlowTarget) =>
    bindings.find((b) => b.target === target && b.step === "RESULTS")?.pageId ??
    null;

  const visibleTargets = isMixedCRMode
    ? TARGETS.filter((t) => t.target === "ARTWORKS")
    : TARGETS;

  return (
    <CmsAdminShell
      title={cmsNew("cms.new.auto.title.parcours_recherche", "Parcours recherche")}
      subtitle={cmsNew("cms.new.auto.subtitle.templates_pour_les_pages_de_resultats_de_recherche_catalogue", "Templates pour les pages de résultats de recherche catalogue.")}
    >
      <div className="space-y-6 px-6 py-4">
        <p className="text-sm text-slate-600">{cmsPhrase(cmsNew, "searchFlowIntro")}</p>

        {flowQuery.isLoading || pagesQuery.isLoading ? (
          <p className="text-sm text-slate-500">{cmsNew("cms.new.auto.text.chargement", "Chargement…")}</p>
        ) : (
          <div className="grid gap-4 md:grid-cols-2">
            {visibleTargets.map(({ target }) => (
              <FlowCell
                key={target}
                target={target}
                value={pageIdFor(target)}
                templatePages={templatePages}
                saving={saveMutation.isPending}
                creating={createTemplateMutation.isPending}
                onAssign={(pageId) =>
                  saveMutation.mutate({ target, step: "RESULTS", pageId })
                }
                onCreateTemplate={() =>
                  createTemplateMutation.mutate({ target, step: "RESULTS" })
                }
              />
            ))}
          </div>
        )}
      </div>
    </CmsAdminShell>
  );
}
